/**
 * React Query Mutation Hook: Deploy Branch Files
 *
 * Deploys multiple branch files to Salesforce in a single atomic operation.
 * Returns either a started deployment or the first conflict found.
 *
 * Conflict resolution loop:
 *   1. Call with branchIds → conflict returned
 *   2. Resolve via mergeBranch → that branch merges to main
 *   3. Remove resolved branch from ids, retry with remaining
 *   4. No conflict → deployment starts → track via SSE
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { graphqlRequest } from '@/services/deployment/client';
import { DEPLOY_BRANCH_FILES_MUTATION } from '@/services/metadata_v1/queries';
import type {
  DeployBranchFilesInput,
  DeployBranchFilesResult,
  DeployBranchFilesResponse,
  DeployBranchFilesVariables,
} from '@/services/metadata_v1/types';

async function deployBranchFiles(
  input: DeployBranchFilesInput
): Promise<DeployBranchFilesResult> {
  const response = await graphqlRequest<DeployBranchFilesResponse>(
    DEPLOY_BRANCH_FILES_MUTATION,
    { input } satisfies DeployBranchFilesVariables
  );
  return response.deployBranchFiles;
}

export function useDeployBranchFiles(): UseMutationResult<
  DeployBranchFilesResult,
  Error,
  DeployBranchFilesInput
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deployBranchFiles,
    onSuccess: (data) => {
      if (data.deployment) {
        // Deployment started — invalidate branch list and deployments
        queryClient.invalidateQueries({ queryKey: ['userBranches'] });
        queryClient.invalidateQueries({ queryKey: ['metadataDeployments'] });
      }
    },
  });
}
